import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import { Medal, AlertTriangle, Sparkles } from 'lucide-react';

/** قائمة عمودية؛ كل طالب في سطر واحد: ميدالية + رقم + اسم + (نقاط) */
export function RankWithMedal({ place, name, compact, points }) {
  const medal = {
    1: 'text-amber-500 drop-shadow-sm',
    2: 'text-slate-400',
    3: 'text-amber-800',
  };
  const num = {
    1: 'text-amber-700',
    2: 'text-slate-600',
    3: 'text-orange-900',
  };
  const iconSize = compact ? 'h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]' : 'h-5 w-5 sm:h-6 sm:w-6';
  const textSize = compact ? 'text-[11px] sm:text-xs' : 'text-sm sm:text-base';

  return (
    <div
      className={`flex w-full min-w-0 items-center gap-2 rounded-xl border border-slate-200/90 bg-white/70 px-2.5 py-1.5 shadow-sm ring-1 ring-slate-900/[0.04] sm:gap-2.5 sm:px-3 sm:py-2 ${textSize} font-semibold text-slate-900`}
    >
      <Medal className={`shrink-0 ${iconSize} ${medal[place]}`} strokeWidth={2.25} aria-hidden />
      <span className={`shrink-0 font-black tabular-nums ${num[place]}`}>{place}</span>
      <span className="min-w-0 flex-1 truncate font-bold">{name || '—'}</span>
      {points != null && points !== '' && (
        <span className="shrink-0 font-semibold tabular-nums text-slate-500">({Number(points)})</span>
      )}
    </div>
  );
}

export function profileName(p) {
  if (!p) return '';
  const a = (p.firstName || '').trim();
  const b = (p.lastName || '').trim();
  return [a, b].filter(Boolean).join(' ') || p.username || '';
}

/** مطابقة الطالب الحالي مع بيانات الـ API (id رقم/نص، أو username) */
export function useIsCurrentSpotlightUser() {
  const { user } = useAuth();

  return useCallback(
    (entry) => {
      if (!entry || !user) return false;
      if (entry.userId != null && user.id != null && String(entry.userId) === String(user.id)) {
        return true;
      }
      const u = (user.username || '').toLowerCase();
      const p = (entry.profile?.username || '').toLowerCase();
      if (u && p && u === p) return true;
      const userFull = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
      const profFull = profileName(entry.profile).toLowerCase();
      if (userFull && profFull && userFull === profFull) return true;
      return false;
    },
    [user]
  );
}

/**
 * بؤرة حماس اليوم للقسم — وضع compact بجانب مربع الترحيب.
 */
const DepartmentSpotlight = ({ compact = false, className = '', hideHeading = false }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { user } = useAuth();
  const isMe = useIsCurrentSpotlightUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/students/enthusiasm/department-spotlight');
        if (!cancelled && res.data?.success) {
          setData(res.data.data);
        } else if (!cancelled) {
          setError('bad');
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status || 'network');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const l1 = data?.leaders?.[0];
  const l2 = data?.leaders?.[1];
  const ci = data?.closingIn;

  const role = useMemo(() => {
    if (!data || !user) return 'none';
    if (isMe(l1)) return 'first';
    if (isMe(l2)) return 'second';
    if (ci && isMe({ userId: ci.userId, profile: ci.profile })) return 'third';
    return 'none';
  }, [data, user, isMe, l1, l2, ci]);

  const labelFor = (entry) => {
    if (!entry) return '—';
    if (isMe(entry)) return t('dashboard.student.spotlightYou');
    return profileName(entry.profile);
  };

  const n1 = l1 ? labelFor(l1) : '';
  const n2 = l2 ? labelFor(l2) : '';
  const nc = ci ? (isMe({ userId: ci.userId, profile: ci.profile }) ? t('dashboard.student.spotlightYou') : profileName(ci.profile)) : '';
  const leaderNameForMsg = l1 && isMe(l1) ? t('dashboard.student.spotlightYou') : profileName(l1?.profile);
  const secondNameForMsg = l2 && isMe(l2) ? t('dashboard.student.spotlightYou') : profileName(l2?.profile);

  const shellClass = compact
    ? `rounded-2xl border-[3px] border-amber-400/35 bg-gradient-to-br from-amber-100/95 via-yellow-50/90 to-orange-50/80 shadow-[0_10px_0_0_rgba(245,158,11,0.18)] px-3 py-3 sm:px-4 sm:py-4 flex flex-col flex-1 min-h-0 min-w-0 justify-between gap-2 ring-1 ring-amber-200/50 ${className}`
    : `rounded-3xl border-[3px] border-slate-900/10 bg-gradient-to-br from-amber-50/90 via-white to-lime-50/70 shadow-[0_8px_0_0_rgba(15,23,42,0.06)] px-4 py-5 sm:px-6 sm:py-6 mb-6 ${className}`;

  if (loading) {
    return (
      <div
        className={`rounded-3xl border-[3px] border-slate-900/10 bg-white/80 animate-pulse min-w-0 ${compact ? 'px-3 py-3 mb-0' : 'px-4 py-6 mb-6'} ${className}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className={`bg-slate-200 rounded ${compact ? 'h-4 w-32 mb-2' : 'h-6 w-48 mb-4'}`} />
        <div className={`bg-slate-100 rounded-xl ${compact ? 'h-16' : 'h-20'}`} />
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  const personalMsgClass = compact
    ? 'rounded-xl border-[2px] px-2 py-1.5 font-bold text-[11px] sm:text-xs leading-snug'
    : 'mb-4 rounded-2xl border-[3px] px-4 py-3 font-bold text-sm sm:text-base leading-snug';

  const msgOthers = (t('dashboard.student.spotlightMsgOthers') || '').trim();
  const showPersonalMsg =
    role === 'first' || role === 'second' || role === 'third' || (role === 'none' && msgOthers.length > 0);

  const personalInner = (
    <>
      {role === 'first' && t('dashboard.student.spotlightMsgYouFirst')}
      {role === 'second' &&
        t('dashboard.student.spotlightMsgYouSecond', { leader: leaderNameForMsg || n1 })}
      {role === 'third' &&
        t('dashboard.student.spotlightMsgYouThird', { second: secondNameForMsg || n2 })}
      {role === 'none' && msgOthers}
    </>
  );

  if (compact) {
    return (
      <section className={shellClass} dir={isRTL ? 'rtl' : 'ltr'} aria-label={t('dashboard.student.spotlightTitle')}>
        {!hideHeading ? (
          <div className="flex min-w-0 items-start gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md shadow-amber-500/30">
              <Sparkles className="h-4 w-4" strokeWidth={2.5} aria-hidden />
            </span>
            <h2 className="text-xs font-black leading-snug text-slate-900 sm:text-sm">
              {t('dashboard.student.spotlightTitle')}
            </h2>
          </div>
        ) : null}

        {showPersonalMsg && (
          <div
            className={`${personalMsgClass} ${
              role === 'first'
                ? 'border-amber-400 bg-amber-100/80 text-amber-950'
                : role === 'second'
                  ? 'border-sky-400 bg-sky-100/80 text-sky-950'
                  : role === 'third'
                    ? 'border-violet-400 bg-violet-100/80 text-violet-950'
                    : 'border-lime-400 bg-lime-100/70 text-lime-950'
            }`}
          >
            {personalInner}
          </div>
        )}

        <div className="min-w-0 text-slate-800">
          <div className="flex flex-col gap-2 min-w-0">
            <RankWithMedal place={1} name={n1} compact points={l1?.total ?? l1?.points} />
            <RankWithMedal place={2} name={n2} compact points={l2?.total ?? l2?.points} />
            {ci && nc && <RankWithMedal place={3} name={nc} compact points={ci?.total} />}
          </div>
        </div>

        <div className="flex gap-1.5 rounded-xl border-[2px] border-rose-200 bg-rose-50/90 px-2 py-1 text-rose-950">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-rose-600" aria-hidden />
          <p className="text-[10px] sm:text-[11px] font-semibold leading-snug">
            {t('dashboard.student.spotlightWarn', {
              first: n1,
              second: n2,
              chaser: nc,
            })}
          </p>
        </div>
      </section>
    );
  }

  /* وضع كامل (غير مستخدم حالياً — احتياطي) */
  return (
    <section
      className={shellClass}
      dir={isRTL ? 'rtl' : 'ltr'}
      aria-label={t('dashboard.student.spotlightTitle')}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
        <div>
          <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 flex items-start gap-2 leading-snug">
            <Sparkles className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" aria-hidden />
            {t('dashboard.student.spotlightTitle')}
          </h2>
        </div>
      </div>

      {showPersonalMsg && (
        <div
          className={`mb-4 rounded-2xl border-[3px] px-4 py-3 font-bold text-sm sm:text-base leading-snug ${
            role === 'first'
              ? 'border-amber-400 bg-amber-100/80 text-amber-950'
              : role === 'second'
                ? 'border-sky-400 bg-sky-100/80 text-sky-950'
                : role === 'third'
                  ? 'border-violet-400 bg-violet-100/80 text-violet-950'
                  : 'border-lime-400 bg-lime-100/70 text-lime-950'
          }`}
        >
          {personalInner}
        </div>
      )}

      <div className="mb-4 min-w-0 rounded-2xl border-[3px] border-slate-900/10 bg-white px-4 py-3 shadow-[0_4px_0_0_rgba(15,23,42,0.08)]">
        <div className="flex min-w-0 flex-col gap-3">
          <RankWithMedal place={1} name={n1} compact={false} points={l1?.total ?? l1?.points} />
          <RankWithMedal place={2} name={n2} compact={false} points={l2?.total ?? l2?.points} />
          {ci && nc && (
            <RankWithMedal place={3} name={nc} compact={false} points={ci?.total ?? ci?.points} />
          )}
        </div>
      </div>

      <div className="flex gap-2 rounded-2xl border-[3px] border-rose-200 bg-rose-50/90 px-3 py-2.5 text-rose-950">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-rose-600" aria-hidden />
        <p className="text-xs sm:text-sm font-semibold leading-snug">
          {t('dashboard.student.spotlightWarn', { first: n1, second: n2, chaser: nc })}
        </p>
      </div>
    </section>
  );
};

export default DepartmentSpotlight;
